import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProducts, checkCustomerExistsByPhone, searchProfiloProductsByModelCode } from '@/lib/supabaseDb';
import type { ProfiloProductResult } from '@/lib/supabaseDb';
import { saveFullQuote, generateQuoteNo, getQuoteById, getQuoteItems, getQuotePaymentPlan, getQuoteNoteTerms } from '@/lib/quoteDb';
import CurrencyInput from '@/components/base/CurrencyInput';
import CityDistrictSelect from '@/components/base/CityDistrictSelect';
import { formatTRY } from '@/lib/currencyFormat';
import { useDebounce } from '@/hooks/useDebounce';
import { useAuth } from '@/contexts/AuthContext';

type ItemCategory = 'furniture' | 'profilo' | 'other';

const furnitureTypes = ['Koltuk Takımı', 'Yatak Odası', 'Yemek Odası', 'TV Ünitesi', 'Genç Odası', 'Berjer', 'Sehpa', 'Vitrin', 'Kitaplık'];
const colors = ['Beyaz', 'Krem', 'Gri', 'Antrasit', 'Ceviz', 'Meşe', 'Maun', 'Siyah', 'Inox', 'Silver'];
const paymentTypes = ['Peşin', 'Nakit', 'Kredi Kartı', 'Havale / EFT', 'Elden Taksit', 'Senet', 'Karma Ödeme'];
const CATEGORY_LABELS: Record<ItemCategory, string> = { furniture: 'Mobilya', profilo: 'Profilo', other: 'Diğer' };
const STATUS_LIST = ['Taslak', 'Müşteriye Sunuldu', 'Revize Bekliyor', 'Beklemede', 'Reddedildi', 'İptal Edildi'];

function createEmptyItem(category: ItemCategory) {
  const brand = category === 'furniture' ? 'Polatlar Mobilya' : category === 'profilo' ? 'Profilo' : '';
  return {
    productId: '',
    productName: '',
    productType: '',
    category,
    brand,
    modelCode: '',
    serialNo: '',
    color: '',
    dimensions: '',
    quantity: 1,
    unitPrice: 0,
    unitCost: null as number | null,
  };
}

interface Props {
  editQuoteId?: string | null;
}

export default function NewQuotePage({ editQuoteId = null }: Props) {
  const navigate = useNavigate();
  const { businessCity, businessDistrict } = useAuth();
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [showToast, setShowToast] = useState<string | null>(null);
  const itemListRef = useRef<HTMLDivElement>(null);

  const todayStr = new Date().toISOString().split('T')[0];
  const defaultValidUntil = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultDeliveryDate = tomorrow.toISOString().split('T')[0];
  const nextHour = (new Date().getHours() + 1) % 24;
  const defaultDeliveryTime = `${String(nextHour).padStart(2, '0')}:00`;

  // Prospect form
  const [prospect, setProspect] = useState({
    prospectName: '', phone: '', secondaryPhone: '', identityNo: '',
    city: '', district: '', neighborhood: '', address: '', customerNote: '',
  });
  const [existingCustomer, setExistingCustomer] = useState<any>(null);
  const [useExistingCustomer, setUseExistingCustomer] = useState(false);
  const [phoneCheckDone, setPhoneCheckDone] = useState(false);

  // İşletmenin kayıtlı il/ilçesini varsayılan öneri olarak doldur (yeni
  // teklif için) — düzenleme modunda veya kullanıcı zaten bir şey girdiyse
  // dokunulmaz.
  useEffect(() => {
    if (editQuoteId) return;
    setProspect((p) => (p.city ? p : { ...p, city: businessCity || p.city, district: businessDistrict || p.district }));
  }, [editQuoteId, businessCity, businessDistrict]);

  // Quote form
  const [quoteDate, setQuoteDate] = useState(todayStr);
  const [validUntil, setValidUntil] = useState(defaultValidUntil);
  const [status, setStatus] = useState('Taslak');
  const [preparedBy, setPreparedBy] = useState('Hikmet Polat');
  const [publicNote, setPublicNote] = useState('');
  const [internalNote, setInternalNote] = useState('');

  // Price
  const [discountAmount, setDiscountAmount] = useState(0);
  const [shippingAmount, setShippingAmount] = useState(0);
  const [assemblyAmount, setAssemblyAmount] = useState(0);

  // Payment plan
  const [paymentType, setPaymentType] = useState('Peşin');
  const [plannedDeposit, setPlannedDeposit] = useState(0);
  const [installmentCount, setInstallmentCount] = useState(3);
  const [firstPaymentDate, setFirstPaymentDate] = useState('');
  const [paymentPlanType, setPaymentPlanType] = useState<'equal' | 'custom'>('equal');
  const [customPlans, setCustomPlans] = useState<Array<{ id: string; no: number; dueDate: string; amount: number; method: string; description: string }>>([]);

  // Note terms (Senet bilgileri)
  const [noteTerms, setNoteTerms] = useState({
    bookNo: '',
    pageNo: '',
    sequenceNo: '',
    dueDate: '',
    guarantorName: '',
    guarantorPhone: '',
    noteDescription: '',
  });

  // Delivery
  const [deliveryDate, setDeliveryDate] = useState(defaultDeliveryDate);
  const [deliveryTime, setDeliveryTime] = useState(defaultDeliveryTime);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [needAssembly, setNeedAssembly] = useState(false);
  const [deliveryNote, setDeliveryNote] = useState('');

  // Items
  const [items, setItems] = useState([createEmptyItem('furniture')]);
  const [itemSearch, setItemSearch] = useState<{ activeIdx: number | null; term: string }>({ activeIdx: null, term: '' });

  // Profilo model kodu arama
  const [profiloModelSearch, setProfiloModelSearch] = useState<{ activeIdx: number | null; term: string }>({ activeIdx: null, term: '' });
  const debouncedProfiloSearch = useDebounce(profiloModelSearch.term, 400);
  const [profiloSearchResults, setProfiloSearchResults] = useState<Record<number, ProfiloProductResult[]>>();
  const [profiloSearchLoading, setProfiloSearchLoading] = useState<Record<number, boolean>>();
  const [profiloNotFound, setProfiloNotFound] = useState<Record<number, boolean>>();
  const [profiloSearchDropdown, setProfiloSearchDropdown] = useState<number | null>(null);

  // Load products
  useEffect(() => {
    async function load() {
      const prodRes = await getProducts();
      setProducts(prodRes.data || []);
      setLoading(false);
    }
    load();
  }, []);

  // Profilo model kodu debounced arama
  useEffect(() => {
    const idx = profiloModelSearch.activeIdx;
    const term = debouncedProfiloSearch;
    if (idx === null || !term || term.length < 2) return;
    let cancelled = false;
    setProfiloSearchLoading(prev => ({ ...prev, [idx]: true }));
    setProfiloNotFound(prev => ({ ...prev, [idx]: false }));
    searchProfiloProductsByModelCode(term).then(res => {
      if (cancelled) return;
      setProfiloSearchLoading(prev => ({ ...prev, [idx]: false }));
      const results = res.data || [];
      setProfiloSearchResults(prev => ({ ...prev, [idx]: results }));
      if (results.length === 0) setProfiloNotFound(prev => ({ ...prev, [idx]: true }));
      setProfiloSearchDropdown(idx);
    }).catch(() => {
      if (cancelled) return;
      setProfiloSearchLoading(prev => ({ ...prev, [idx]: false }));
    });
    return () => { cancelled = true; };
  }, [debouncedProfiloSearch, profiloModelSearch.activeIdx]);

  // Load existing quote for edit
  useEffect(() => {
    if (editQuoteId) {
      (async () => {
        const [qRes, iRes, pRes] = await Promise.all([
          getQuoteById(editQuoteId),
          getQuoteItems(editQuoteId),
          getQuotePaymentPlan(editQuoteId),
        ]);
        const q = qRes.data;
        if (q) {
          setProspect({
            prospectName: q.prospect_name || '',
            phone: q.phone || '',
            secondaryPhone: q.secondary_phone || '',
            identityNo: q.identity_no || '',
            city: q.city || '',
            district: q.district || '',
            neighborhood: q.neighborhood || '',
            address: q.address || '',
            customerNote: q.customer_note || '',
          });
          setQuoteDate(q.quote_date || todayStr);
          setValidUntil(q.valid_until || defaultValidUntil);
          setStatus(q.status || 'Taslak');
          setPreparedBy(q.prepared_by || 'Hikmet Polat');
          setPublicNote(q.public_note || '');
          setInternalNote(q.internal_note || '');
          setDiscountAmount(parseFloat(q.discount_amount) || 0);
          setShippingAmount(parseFloat(q.shipping_amount) || 0);
          setAssemblyAmount(parseFloat(q.assembly_amount) || 0);
          setPaymentType(q.payment_type || 'Peşin');
          setPlannedDeposit(parseFloat(q.planned_deposit) || 0);
          setInstallmentCount(q.installment_count || 3);
          setFirstPaymentDate(q.first_payment_date || '');
          setDeliveryDate(q.delivery_date || '');
          setDeliveryTime(q.delivery_time || '');
          setDeliveryAddress(q.delivery_address || '');
          setNeedAssembly(parseFloat(q.assembly_amount) > 0);
          setDeliveryNote(q.delivery_note || '');

          const its = iRes.data || [];
          if (its.length > 0) {
            setItems(its.map((it: any) => ({
              productId: it.product_id || '',
              productName: it.product_name || '',
              productType: it.product_type || '',
              category: (it.category === 'Mobilya' ? 'furniture' : it.category === 'Profilo' ? 'profilo' : 'other') as ItemCategory,
              brand: it.brand || '',
              modelCode: it.model_code || '',
              serialNo: it.serial_no || '',
              color: it.color || '',
              dimensions: it.dimensions || '',
              quantity: it.quantity || 1,
              unitPrice: parseFloat(it.unit_price) || 0,
              unitCost: it.unit_cost != null ? parseFloat(it.unit_cost) : null,
            })));
          }

          const plans = pRes.data || [];
          if (plans.length > 0) {
            setPaymentPlanType('custom');
            setCustomPlans(plans.map((p: any) => ({
              id: p.id || crypto.randomUUID(),
              no: p.installment_no,
              dueDate: p.due_date || '',
              amount: parseFloat(p.amount) || 0,
              method: p.payment_method || '',
              description: p.description || '',
            })));
          }

          // Load note terms if editing
          if (editQuoteId) {
            const { getQuoteNoteTerms } = await import('@/lib/quoteDb');
            const { data: ntData } = await getQuoteNoteTerms(editQuoteId);
            if (ntData) {
              setNoteTerms({
                bookNo: ntData.book_no || '',
                pageNo: ntData.page_no || '',
                sequenceNo: ntData.sequence_no || '',
                dueDate: ntData.due_date || '',
                guarantorName: ntData.guarantor_name || '',
                guarantorPhone: ntData.guarantor_phone || '',
                noteDescription: ntData.notes || '',
              });
            }
          }
        }
      })();
    }
  }, [editQuoteId]);

  // Check phone for existing customer
  const checkPhone = async () => {
    if (!prospect.phone || prospect.phone.length < 10) return;
    const { data } = await checkCustomerExistsByPhone(prospect.phone);
    setPhoneCheckDone(true);
    if (data) {
      setExistingCustomer(data);
    } else {
      setExistingCustomer(null);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      if (prospect.phone && prospect.phone.length >= 10 && !phoneCheckDone) {
        checkPhone();
      }
    }, 800);
    return () => clearTimeout(t);
  }, [prospect.phone]);

  // Items management
  const addItem = (category: ItemCategory) => {
    setItems(prev => [createEmptyItem(category), ...prev]);
    setShowCategoryPicker(false);
    setTimeout(() => itemListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };
  const removeItem = (idx: number) => { if (items.length > 1) setItems(items.filter((_, i) => i !== idx)); };
  const updateItem = (idx: number, field: string, value: string | number | boolean) => {
    const newItems = [...items];
    (newItems[idx] as any)[field] = value;
    setItems(newItems);
  };

  const getProductsByCategory = (category: ItemCategory) => {
    const catLabel = CATEGORY_LABELS[category];
    return products.filter((p: any) => p.category === catLabel);
  };

  const handleProductSelect = (idx: number, productId: string) => {
    if (!productId) {
      updateItem(idx, 'productId', '');
      updateItem(idx, 'productName', '');
      updateItem(idx, 'productType', '');
      updateItem(idx, 'brand', '');
      updateItem(idx, 'modelCode', '');
      updateItem(idx, 'serialNo', '');
      updateItem(idx, 'color', '');
      updateItem(idx, 'dimensions', '');
      return;
    }
    const prod = products.find((p: any) => p.id === productId);
    if (!prod) return;
    updateItem(idx, 'productId', prod.id);
    updateItem(idx, 'productName', prod.name || '');
    updateItem(idx, 'productType', prod.product_type || '');
    updateItem(idx, 'brand', prod.brand || '');
    updateItem(idx, 'modelCode', prod.model_code || '');
    updateItem(idx, 'serialNo', prod.serial_number || '');
    updateItem(idx, 'color', prod.color || '');
    updateItem(idx, 'dimensions', prod.size || '');
    updateItem(idx, 'unitPrice', prod.sale_price || 0);
  };

  // Profilo model kodu arama handler'ları
  const handleProfiloProductSelect = (idx: number, prod: ProfiloProductResult) => {
    updateItem(idx, 'productId', prod.id);
    updateItem(idx, 'productName', prod.name || '');
    updateItem(idx, 'productType', prod.product_type || '');
    updateItem(idx, 'brand', prod.brand || 'Profilo');
    updateItem(idx, 'modelCode', prod.model_code || '');
    updateItem(idx, 'color', prod.color || '');
    updateItem(idx, 'dimensions', prod.capacity || prod.size || '');
    updateItem(idx, 'serialNo', '');
    updateItem(idx, 'unitPrice', prod.sale_price || 0);
    setProfiloModelSearch({ activeIdx: null, term: '' });
    setProfiloSearchDropdown(null);
    setProfiloNotFound(prev => ({ ...prev, [idx]: false }));
    setProfiloSearchResults(prev => ({ ...prev, [idx]: [] }));
  };

  const handleProfiloManualContinue = (idx: number) => {
    updateItem(idx, 'modelCode', profiloModelSearch.term || '');
    updateItem(idx, 'productId', '');
    updateItem(idx, 'productName', '');
    setProfiloModelSearch({ activeIdx: null, term: '' });
    setProfiloSearchDropdown(null);
    setProfiloNotFound(prev => ({ ...prev, [idx]: false }));
  };

  // Manuel toplam girişi
  const [useManualTotal, setUseManualTotal] = useState(false);
  const [manualTotal, setManualTotal] = useState(0);

  // Calculations
  const subtotal = items.reduce((sum, i) => sum + i.quantity * i.unitPrice, 0);
  const effectiveSubtotal = useManualTotal && manualTotal > 0 ? manualTotal : subtotal;
  const totalCost = items.reduce((sum, i) => sum + (i.unitCost || 0) * i.quantity, 0);
  const afterDiscount = effectiveSubtotal - discountAmount;
  const totalAmount = afterDiscount + shippingAmount + assemblyAmount;
  const estimatedProfit = totalAmount - totalCost - shippingAmount - assemblyAmount;
  const plannedRemaining = totalAmount - plannedDeposit;

  // Custom plan helpers
  const customTotal = customPlans.reduce((s, p) => s + p.amount, 0);
  const customDiff = plannedRemaining - customTotal;
  const customIsBalanced = Math.abs(customDiff) < 0.01;

  const addCustomPlanRow = () => {
    const no = customPlans.length + 1;
    const lastDate = customPlans.length > 0 ? customPlans[customPlans.length - 1].dueDate : (firstPaymentDate || quoteDate);
    const nextDate = lastDate ? new Date(lastDate) : new Date();
    nextDate.setMonth(nextDate.getMonth() + 1);
    setCustomPlans([...customPlans, { id: crypto.randomUUID(), no, dueDate: nextDate.toISOString().split('T')[0], amount: 0, method: paymentType, description: '' }]);
  };

  const updateCustomPlan = (id: string, field: string, value: any) => {
    setCustomPlans(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const removeCustomPlan = (id: string) => {
    setCustomPlans(prev => prev.filter(p => p.id !== id).map((p, i) => ({ ...p, no: i + 1 })));
  };

  const handleSubmit = async (draft: boolean = false) => {
    if (saving) return;
    if (!prospect.prospectName.trim()) { setShowToast('Lütfen müşteri adayı adını girin'); setTimeout(() => setShowToast(null), 3000); return; }
    if (!prospect.phone.trim()) { setShowToast('Lütfen telefon girin'); setTimeout(() => setShowToast(null), 3000); return; }
    if (useManualTotal && manualTotal <= 0) { setShowToast('Lütfen manuel toplam tutarını giriniz.'); setTimeout(() => setShowToast(null), 3000); return; }
    if (!useManualTotal && items.some(i => !i.productName || i.unitPrice <= 0)) { setShowToast('Lütfen tüm ürün adı ve fiyat alanlarını doldurun'); setTimeout(() => setShowToast(null), 3000); return; }
    if (useManualTotal && items.some(i => !i.productName)) { setShowToast('Lütfen tüm ürün adı alanlarını doldurun'); setTimeout(() => setShowToast(null), 3000); return; }
    if (paymentPlanType === 'custom' && customPlans.length > 0 && !customIsBalanced && !draft) {
      setShowToast('Taksit planı toplamı kalan tutar ile eşleşmiyor');
      setTimeout(() => setShowToast(null), 4000);
      return;
    }

    setSaving(true);
    try {
      const quoteData = {
        prospect_name: prospect.prospectName,
        phone: prospect.phone,
        secondary_phone: prospect.secondaryPhone || null,
        identity_no: prospect.identityNo || null,
        city: prospect.city || null,
        district: prospect.district || null,
        neighborhood: prospect.neighborhood || null,
        address: prospect.address || null,
        customer_note: prospect.customerNote || null,
        existing_customer_id: useExistingCustomer && existingCustomer ? existingCustomer.id : null,
        quote_date: quoteDate,
        valid_until: validUntil,
        status: draft ? 'Taslak' : status,
        subtotal: effectiveSubtotal,
        discount_amount: discountAmount,
        shipping_amount: shippingAmount,
        assembly_amount: assemblyAmount,
        tax_amount: 0,
        total_amount: totalAmount,
        total_cost: totalCost,
        estimated_profit: estimatedProfit,
        payment_type: paymentType,
        planned_deposit: plannedDeposit,
        planned_remaining: plannedRemaining,
        installment_count: paymentPlanType === 'equal' ? installmentCount : customPlans.length,
        first_payment_date: firstPaymentDate || null,
        delivery_date: deliveryDate || null,
        delivery_time: deliveryTime || null,
        delivery_address: deliveryAddress || null,
        delivery_note: deliveryNote || null,
        public_note: publicNote || null,
        internal_note: internalNote || null,
        prepared_by: preparedBy,
        revision_no: editQuoteId ? undefined : 1,
      };

      const quoteItems = items.map(it => ({
        category: CATEGORY_LABELS[it.category],
        product_id: it.productId || null,
        product_type: it.productType || null,
        brand: it.brand || null,
        product_name: it.productName,
        model_code: it.modelCode || null,
        serial_no: it.serialNo || null,
        color: it.color || null,
        dimensions: it.dimensions || null,
        quantity: it.quantity,
        unit_cost: it.unitCost,
        unit_price: it.unitPrice,
        discount_amount: 0,
        line_total: it.quantity * it.unitPrice,
        description: null,
      }));

      let paymentPlan: any[] = [];
      if (paymentPlanType === 'custom' && customPlans.length > 0) {
        paymentPlan = customPlans.map(p => ({
          installment_no: p.no,
          due_date: p.dueDate || null,
          amount: p.amount,
          payment_method: p.method || paymentType,
          description: p.description || null,
        }));
      }

      // Build note terms if payment type is Senet
      const noteTermsData = paymentType === 'Senet' ? {
        book_no: noteTerms.bookNo || null,
        page_no: noteTerms.pageNo || null,
        sequence_no: noteTerms.sequenceNo || null,
        due_date: noteTerms.dueDate || null,
        amount: plannedRemaining,
        guarantor_name: noteTerms.guarantorName || null,
        guarantor_phone: noteTerms.guarantorPhone || null,
        notes: noteTerms.noteDescription || null,
      } : null;

      const { data: created, error } = await saveFullQuote(quoteData, quoteItems, paymentPlan, noteTermsData, editQuoteId);
      if (error) {
        setShowToast('Hata: ' + error);
        setTimeout(() => setShowToast(null), 3000);
      } else {
        setShowToast(draft ? 'Taslak kaydedildi' : 'Teklif başarıyla kaydedildi');
        setTimeout(() => {
          setShowToast(null);
          navigate(created ? `/quotes/${created.id}` : '/quotes');
        }, 1500);
      }
    } catch (err: any) {
      setShowToast('Hata: ' + (err.message || 'Beklenmedik hata'));
      setTimeout(() => setShowToast(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[60vh]">
      <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
        <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
      <p className="text-sm text-foreground-500">Yükleniyor...</p>
    </div>
  );

  const categoryBadgeColor = (cat: ItemCategory) => {
    if (cat === 'furniture') return 'bg-amber-100 text-amber-700 border-amber-200';
    if (cat === 'profilo') return 'bg-sky-100 text-sky-700 border-sky-200';
    return 'bg-foreground-100 text-foreground-600 border-foreground-200';
  };

  return (
    <div className="space-y-5 max-w-4xl mx-auto p-4 md:p-6">
      {showToast && (
        <div className={`fixed top-4 right-4 z-[100] px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 ${showToast.includes('başarıyla') || showToast.includes('kaydedildi') ? 'bg-emerald-500' : 'bg-amber-500'} text-white animate-fade-in`}>
          <i className={showToast.includes('başarıyla') || showToast.includes('kaydedildi') ? 'ri-check-line' : 'ri-error-warning-line'}></i>
          <span className="text-sm font-medium">{showToast}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/quotes')} className="w-9 h-9 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer">
          <i className="ri-arrow-left-line text-foreground-600"></i>
        </button>
        <div>
          <h2 className="text-xl font-bold text-foreground-900 font-heading">{editQuoteId ? 'Teklifi Düzenle' : 'Yeni Teklif'}</h2>
          <p className="text-sm text-foreground-400">Teklif henüz satış, ödeme, stok veya kasa kaydı oluşturmaz</p>
        </div>
      </div>

      <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 space-y-6">

        {/* ── MÜŞTERİ ADAYI ── */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="w-8 h-8 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-user-3-line text-background-50 text-sm"></i>
            </span>
            <h3 className="text-sm font-semibold text-foreground-900">Müşteri Adayı Bilgileri</h3>
            {existingCustomer && !useExistingCustomer && (
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-xs text-accent-600 bg-accent-50 px-2 py-1 rounded-full">
                  Bu telefon kayıtlı: {existingCustomer.full_name}
                </span>
                <button onClick={() => setUseExistingCustomer(true)} className="text-xs text-primary-500 hover:text-primary-600 font-medium cursor-pointer whitespace-nowrap">
                  Mevcut müşteriye bağla
                </button>
              </div>
            )}
            {useExistingCustomer && existingCustomer && (
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-xs text-primary-600 bg-primary-50 px-2 py-1 rounded-full flex items-center gap-1">
                  <i className="ri-link text-sm"></i>
                  {existingCustomer.full_name} - {existingCustomer.customer_no}
                </span>
                <button onClick={() => setUseExistingCustomer(false)} className="text-xs text-foreground-500 hover:text-foreground-600 cursor-pointer whitespace-nowrap">
                  Bağlantıyı kaldır
                </button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground-500 mb-1.5">Ad Soyad <span className="text-red-400">*</span></label>
              <input type="text" value={prospect.prospectName}
                onChange={e => { setProspect({ ...prospect, prospectName: e.target.value }); setPhoneCheckDone(false); }}
                className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="Ad Soyad" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-500 mb-1.5">Telefon <span className="text-red-400">*</span></label>
              <input type="text" value={prospect.phone}
                onChange={e => { setProspect({ ...prospect, phone: e.target.value }); setPhoneCheckDone(false); }}
                className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="05XX XXX XX XX" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-500 mb-1.5">İkinci Telefon</label>
              <input type="text" value={prospect.secondaryPhone}
                onChange={e => setProspect({ ...prospect, secondaryPhone: e.target.value })}
                className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="05XX XXX XX XX" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-500 mb-1.5">TC Kimlik No</label>
              <input type="text" value={prospect.identityNo}
                onChange={e => setProspect({ ...prospect, identityNo: e.target.value })}
                className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="11 haneli" />
            </div>
            <div className="sm:col-span-2">
              <CityDistrictSelect
                city={prospect.city}
                district={prospect.district}
                neighborhood={prospect.neighborhood}
                onCityChange={(city) => setProspect({ ...prospect, city })}
                onDistrictChange={(district) => setProspect({ ...prospect, district })}
                onNeighborhoodChange={(neighborhood) => setProspect({ ...prospect, neighborhood })}
                selectClassName="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 cursor-pointer"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-foreground-500 mb-1.5">Adres</label>
              <textarea value={prospect.address}
                onChange={e => setProspect({ ...prospect, address: e.target.value })}
                className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 resize-none" rows={2} placeholder="Açık adres"></textarea>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-500 mb-1.5">Müşteri Notu</label>
              <textarea value={prospect.customerNote}
                onChange={e => setProspect({ ...prospect, customerNote: e.target.value })}
                className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 resize-none" rows={2} placeholder="Özel notlar"></textarea>
            </div>
          </div>
        </div>

        {/* ── ÜRÜNLER ── */}
        <div ref={itemListRef}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-accent-500 flex items-center justify-center">
                <i className="ri-shopping-cart-2-line text-background-50 text-sm"></i>
              </span>
              <h3 className="text-sm font-semibold text-foreground-900">Ürünler</h3>
            </div>
            <button onClick={() => setShowCategoryPicker(true)} className="text-xs text-primary-500 hover:text-primary-600 font-medium cursor-pointer flex items-center gap-1">
              <i className="ri-add-line"></i> Ürün Ekle
            </button>
          </div>
          <div className="space-y-3">
            {items.map((item, idx) => {
              const isFurniture = item.category === 'furniture';
              const isProfilo = item.category === 'profilo';
              const isOther = item.category === 'other';
              const catProducts = getProductsByCategory(item.category);

              return (
                <div key={`${idx}-${item.category}`} className="bg-background-100/50 rounded-xl p-4 border border-background-200/50">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-foreground-500">Ürün {idx + 1}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${categoryBadgeColor(item.category)}`}>
                        {CATEGORY_LABELS[item.category]}
                      </span>
                    </div>
                    {items.length > 1 && (
                      <button onClick={() => removeItem(idx)} className="w-6 h-6 rounded flex items-center justify-center cursor-pointer text-red-400 hover:bg-red-50">
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {/* Profilo: Model Kodu arama (Cins alanı kaldırıldı) */}
                    {isProfilo ? (
                      <div className="relative col-span-2 sm:col-span-2 lg:col-span-2">
                        <input type="text"
                          value={profiloModelSearch.activeIdx === idx ? profiloModelSearch.term : (item.modelCode || '')}
                          onChange={e => {
                            const val = e.target.value;
                            setProfiloModelSearch({ activeIdx: idx, term: val });
                            if (item.productId) { updateItem(idx, 'productId', ''); updateItem(idx, 'productName', ''); updateItem(idx, 'productType', ''); }
                            updateItem(idx, 'modelCode', val);
                          }}
                          onFocus={() => { setProfiloModelSearch({ activeIdx: idx, term: item.modelCode || '' }); setProfiloSearchDropdown(idx); }}
                          onBlur={() => setTimeout(() => setProfiloSearchDropdown(null), 250)}
                          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 pr-10"
                          placeholder="Model kodu yaz..." autoComplete="off" />
                        {profiloSearchLoading?.[idx] && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}
                        {profiloSearchDropdown === idx && (
                          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-background-50 border border-background-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                            {profiloSearchLoading?.[idx] ? (
                              <div className="px-4 py-3 text-center text-sm text-foreground-400">Aranıyor...</div>
                            ) : profiloNotFound?.[idx] ? (
                              <div className="p-3">
                                <div className="flex items-start gap-2 text-sm">
                                  <i className="ri-information-line text-amber-500 mt-0.5 flex-shrink-0 w-4 h-4 flex items-center justify-center"></i>
                                  <div>
                                    <p className="text-amber-700 font-medium">Bu model koduyla kayıtlı Profilo ürünü bulunamadı.</p>
                                    <button type="button" onMouseDown={e => { e.preventDefault(); handleProfiloManualContinue(idx); }} className="mt-1 px-3 py-1.5 text-xs rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 font-medium cursor-pointer whitespace-nowrap">Manuel devam et</button>
                                  </div>
                                </div>
                              </div>
                            ) : (profiloSearchResults?.[idx] || []).length > 0 ? (
                              (profiloSearchResults?.[idx] || []).map((prod: ProfiloProductResult) => (
                                <button key={prod.id} type="button" onMouseDown={e => { e.preventDefault(); handleProfiloProductSelect(idx, prod); }} className="w-full text-left px-3 py-3 hover:bg-primary-50 cursor-pointer border-b border-background-100 last:border-0 transition-colors">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-semibold text-foreground-800">{prod.model_code}</span>
                                        <span className="text-xs text-foreground-400 truncate">{prod.name}</span>
                                      </div>
                                      <div className="flex items-center gap-2 mt-1.5 text-xs flex-wrap">
                                        {prod.product_type && <span className="bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-md text-[10px] font-medium">{prod.product_type}</span>}
                                        {prod.sale_price > 0 && <span className="text-emerald-600 font-medium">Satış: {formatTRY(prod.sale_price)}</span>}
                                        {prod.purchase_price > 0 && <span className="text-amber-600">Maliyet: {formatTRY(prod.purchase_price)}</span>}
                                      </div>
                                    </div>
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap flex-shrink-0 mt-0.5 ${(prod.stock_quantity || 0) > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                      Stok: {prod.stock_quantity || 0}
                                    </span>
                                  </div>
                                </button>
                              ))
                            ) : null}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        {!isFurniture && (
                          <input type="text" value={item.productType} onChange={e => updateItem(idx, 'productType', e.target.value)}
                            className="w-full px-2.5 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="Ürün cinsi" />
                        )}
                      </div>
                    )}

                    {isOther ? (
                      <div><input type="text" value={item.brand} onChange={e => updateItem(idx, 'brand', e.target.value)}
                        className="w-full px-2.5 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="Marka" /></div>
                    ) : (
                      <div><div className="w-full px-2.5 py-2 text-sm bg-background-100/70 border border-background-200/70 rounded-lg text-foreground-500 font-medium">{item.brand || '—'}</div></div>
                    )}

                    {isProfilo && item.productName ? (
                      <div className="col-span-2 sm:col-span-1 lg:col-span-1">
                        <div className="w-full px-2.5 py-2 text-sm bg-primary-50 border border-primary-200 rounded-lg text-foreground-700 font-medium truncate" title={item.productName}>
                          {item.productName}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <input type="text" value={item.productName} onChange={e => updateItem(idx, 'productName', e.target.value)}
                          className="w-full px-2.5 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder={isProfilo ? 'Ürün adı (otomatik)' : 'Ürün adı'} />
                      </div>
                    )}

                    {isProfilo && (
                      <div><input type="text" value={item.serialNo} onChange={e => updateItem(idx, 'serialNo', e.target.value)}
                        className="w-full px-2.5 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="Seri no" /></div>
                    )}

                    <div>
                      <select value={item.color} onChange={e => updateItem(idx, 'color', e.target.value)}
                        className="w-full px-2.5 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 cursor-pointer">
                        <option value="">Renk</option>
                        {colors.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>

                    <div><input type="text" value={item.dimensions} onChange={e => updateItem(idx, 'dimensions', e.target.value)}
                      className="w-full px-2.5 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="Ölçü" /></div>

                    <div><input type="number" min="1" value={item.quantity} onChange={e => updateItem(idx, 'quantity', parseInt(e.target.value) || 1)}
                      className="w-full px-2.5 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" /></div>

                    <div><CurrencyInput value={String(item.unitPrice || '')} onChange={v => updateItem(idx, 'unitPrice', parseFloat(v) || 0)}
                      className="w-full px-2.5 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="Birim fiyat" /></div>

                    <div><CurrencyInput value={item.unitCost != null ? String(item.unitCost) : ''} onChange={v => updateItem(idx, 'unitCost', parseFloat(v) || 0)}
                      className="w-full px-2.5 py-2 text-sm bg-amber-50 border border-amber-200 rounded-lg text-foreground-700" placeholder="Birim maliyet" /></div>
                  </div>
                  <div className="mt-2 text-right text-xs text-foreground-500">
                    Satır toplamı: <span className="font-medium text-foreground-700">{formatTRY(item.quantity * item.unitPrice)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── FİYAT HESAPLAMA ── */}
        <div className="bg-background-100/50 rounded-xl p-4 space-y-2">
          <div className="flex items-center justify-between text-sm"><span className="text-foreground-500">Ara Toplam</span><span className={`font-medium ${useManualTotal ? 'text-foreground-300 line-through' : ''}`}>{formatTRY(subtotal)}</span></div>
          <div className="border border-dashed border-amber-300 bg-amber-50/30 rounded-xl p-3 space-y-2">
            <div className="flex items-center gap-2">
              <input type="checkbox" id="manualTotalToggleQuote" checked={useManualTotal} onChange={e => { setUseManualTotal(e.target.checked); if (!e.target.checked) setManualTotal(0); }}
                className="w-4 h-4 rounded border-amber-300 text-amber-500 focus:ring-amber-400 cursor-pointer" />
              <label htmlFor="manualTotalToggleQuote" className="text-sm font-medium text-amber-800 cursor-pointer whitespace-nowrap">Manuel Toplam Girişi</label>
            </div>
            {useManualTotal && (
              <>
                <p className="text-xs text-amber-600">Birim fiyat girmeden, teklifin toplam tutarını elle girin. Kâr, toplamdan maliyetler çıkarılarak otomatik hesaplanır.</p>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">Toplam Tutar</span>
                  <CurrencyInput value={String(manualTotal || '')} onChange={v => setManualTotal(parseFloat(v) || 0)}
                    className="flex-1 px-3 py-2.5 text-base font-semibold bg-amber-50 border-2 border-amber-300 rounded-lg text-foreground-800" placeholder="₺" />
                </div>
              </>
            )}
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-foreground-500">Ürün İndirimi</span>
            <CurrencyInput value={String(discountAmount || '')} onChange={v => setDiscountAmount(parseFloat(v) || 0)}
              className="w-28 px-2.5 py-1.5 text-sm text-right bg-background-50 border border-background-200 rounded-lg text-foreground-700" />
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-foreground-500">Nakliye</span>
            <CurrencyInput value={String(shippingAmount || '')} onChange={v => setShippingAmount(parseFloat(v) || 0)}
              className="w-28 px-2.5 py-1.5 text-sm text-right bg-background-50 border border-background-200 rounded-lg text-foreground-700" />
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-foreground-500">Montaj</span>
            <CurrencyInput value={String(assemblyAmount || '')} onChange={v => setAssemblyAmount(parseFloat(v) || 0)}
              className="w-28 px-2.5 py-1.5 text-sm text-right bg-background-50 border border-background-200 rounded-lg text-foreground-700" />
          </div>
          <div className="flex items-center justify-between text-base border-t border-background-200 pt-3">
            <span className="font-semibold">{useManualTotal && manualTotal > 0 ? 'Genel Toplam' : 'Toplam'}</span>
            <span className={`font-bold text-lg ${useManualTotal && manualTotal > 0 ? 'text-amber-700' : ''}`}>{formatTRY(totalAmount)}</span>
          </div>
          {totalCost > 0 && (
            <div className="flex items-center justify-between text-sm text-foreground-600 pt-1">
              <span>Tahmini Maliyet</span>
              <span>{formatTRY(totalCost)}</span>
            </div>
          )}
          {totalCost > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span>Tahmini Kâr</span>
              <span className={`font-medium ${estimatedProfit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{formatTRY(estimatedProfit)}</span>
            </div>
          )}
          {useManualTotal && manualTotal > 0 && (
            <div className="flex items-center justify-between text-sm border-t border-amber-200 pt-2">
              <span className="text-foreground-500">Manuel Toplam</span>
              <span className="font-medium text-amber-700">{formatTRY(manualTotal)}</span>
            </div>
          )}
        </div>

        {/* ── ÖDEME ŞEKLİ ── */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="w-8 h-8 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-bank-card-line text-background-50 text-sm"></i>
            </span>
            <h3 className="text-sm font-semibold text-foreground-900">Ödeme Şekli (Planlanan)</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground-500 mb-1.5">Ödeme Tipi</label>
              <select value={paymentType} onChange={e => setPaymentType(e.target.value)}
                className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 cursor-pointer">
                {paymentTypes.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-500 mb-1.5">Planlanan Peşinat</label>
              <CurrencyInput value={String(plannedDeposit || '')} onChange={v => setPlannedDeposit(parseFloat(v) || 0)}
                className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-500 mb-1.5">Kalan Tutar</label>
              <div className="w-full px-3 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg font-medium text-foreground-700">
                {formatTRY(plannedRemaining)}
              </div>
            </div>
          </div>

          {(paymentType === 'Senet') && (
            <div className="mt-4 bg-background-100/50 rounded-xl p-4 border border-background-200/50 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-7 h-7 rounded-lg bg-amber-500 flex items-center justify-center">
                  <i className="ri-bill-line text-background-50 text-xs"></i>
                </span>
                <span className="text-sm font-semibold text-foreground-900">Senet Bilgileri</span>
              </div>

              <div className="bg-background-50 rounded-lg p-3 mb-2 flex items-center justify-between text-sm">
                <span className="text-foreground-500">Senede Bağlanacak Tutar</span>
                <span className="font-bold text-foreground-900 text-base">{formatTRY(plannedRemaining)}</span>
              </div>

              <div className="text-xs text-amber-600 bg-amber-50 rounded-lg p-2 flex items-start gap-1.5">
                <i className="ri-information-line mt-0.5"></i>
                <span>Senet bilgileri teklif onaylanana kadar yalnızca planlama amaçlıdır. Gerçek senet kaydı onay sırasında oluşturulur.</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1.5">Senet Kitap No <span className="text-red-400">*</span></label>
                  <input type="text" value={noteTerms.bookNo}
                    onChange={e => setNoteTerms({ ...noteTerms, bookNo: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="Kitap no" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1.5">Senet Sayfa No <span className="text-red-400">*</span></label>
                  <input type="text" value={noteTerms.pageNo}
                    onChange={e => setNoteTerms({ ...noteTerms, pageNo: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="Sayfa no" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1.5">Senet Sıra No</label>
                  <input type="text" value={noteTerms.sequenceNo}
                    onChange={e => setNoteTerms({ ...noteTerms, sequenceNo: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="Sıra no" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1.5">Vade Tarihi <span className="text-red-400">*</span></label>
                  <input type="date" value={noteTerms.dueDate}
                    onChange={e => setNoteTerms({ ...noteTerms, dueDate: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" />
                </div>
                <div></div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1.5">Kefil Adı Soyadı</label>
                  <input type="text" value={noteTerms.guarantorName}
                    onChange={e => setNoteTerms({ ...noteTerms, guarantorName: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="Kefil adı" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1.5">Kefil Telefonu</label>
                  <input type="text" value={noteTerms.guarantorPhone}
                    onChange={e => setNoteTerms({ ...noteTerms, guarantorPhone: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="05XX XXX XX XX" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Senet Açıklaması</label>
                <textarea value={noteTerms.noteDescription}
                  onChange={e => setNoteTerms({ ...noteTerms, noteDescription: e.target.value })}
                  maxLength={500}
                  className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 resize-none" rows={2} placeholder="Senet ile ilgili özel notlar"></textarea>
              </div>
            </div>
          )}

          {(paymentType === 'Elden Taksit' || paymentType === 'Taksitli' || paymentType === 'Karma Ödeme') && (
            <div className="mt-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-medium text-foreground-500">Taksit Planı</span>
                <div className="flex gap-1">
                  {(['equal', 'custom'] as const).map(t => (
                    <button key={t} onClick={() => setPaymentPlanType(t)}
                      className={`px-3 py-1 rounded-md text-xs font-medium cursor-pointer transition-colors ${paymentPlanType === t ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'}`}>
                      {t === 'equal' ? 'Eşit' : 'Özel'}
                    </button>
                  ))}
                </div>
              </div>

              {paymentPlanType === 'equal' && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1.5">Taksit Sayısı</label>
                    <select value={installmentCount} onChange={e => setInstallmentCount(parseInt(e.target.value))}
                      className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 cursor-pointer">
                      {[2,3,4,5,6,8,10,12].map(n => <option key={n} value={n}>{n} Taksit</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-500 mb-1.5">İlk Ödeme Tarihi</label>
                    <input type="date" value={firstPaymentDate} onChange={e => setFirstPaymentDate(e.target.value)}
                      className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" />
                  </div>
                  {plannedRemaining > 0 && installmentCount > 0 && (
                    <div>
                      <label className="block text-xs font-medium text-foreground-500 mb-1.5">Taksit Tutarı</label>
                      <div className="w-full px-3 py-2.5 text-sm bg-accent-50 border border-accent-200 rounded-lg font-medium text-accent-800">
                        {formatTRY(Math.ceil(plannedRemaining / installmentCount))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {paymentPlanType === 'custom' && (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-1.5">
                    <button onClick={addCustomPlanRow} className="px-2.5 py-1.5 text-xs rounded-lg bg-primary-50 text-primary-600 hover:bg-primary-100 font-medium cursor-pointer whitespace-nowrap">
                      <i className="ri-add-line mr-1"></i>Taksit Ekle
                    </button>
                  </div>

                  {customPlans.length > 0 && (
                    <div className="overflow-x-auto border border-background-200/70 rounded-xl">
                      <table className="w-full min-w-[500px]">
                        <thead><tr className="border-b border-background-100 bg-background-100/50">
                          <th className="px-3 py-2 text-left text-xs font-medium text-foreground-400">#</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-foreground-400">Tarih</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-foreground-400">Tutar</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-foreground-400">Yöntem</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-foreground-400">Açıklama</th>
                          <th className="px-3 py-2 w-10"></th>
                        </tr></thead>
                        <tbody>
                          {customPlans.map(p => (
                            <tr key={p.id} className="border-b border-background-100 hover:bg-background-50">
                              <td className="px-3 py-2 text-sm text-foreground-400">{p.no}</td>
                              <td className="px-3 py-1.5"><input type="date" value={p.dueDate} onChange={e => updateCustomPlan(p.id, 'dueDate', e.target.value)}
                                className="w-full px-2 py-1.5 text-xs bg-background-50 border border-background-200 rounded text-foreground-700" /></td>
                              <td className="px-3 py-1.5"><CurrencyInput value={String(p.amount || '')} onChange={v => updateCustomPlan(p.id, 'amount', parseFloat(v) || 0)}
                                className="w-24 px-2 py-1.5 text-xs bg-background-50 border border-background-200 rounded text-foreground-700 text-right" /></td>
                              <td className="px-3 py-1.5"><select value={p.method} onChange={e => updateCustomPlan(p.id, 'method', e.target.value)}
                                className="w-full px-2 py-1.5 text-xs bg-background-50 border border-background-200 rounded text-foreground-700 cursor-pointer">
                                <option value="">Seç</option>
                                {paymentTypes.map(m => <option key={m} value={m}>{m}</option>)}
                              </select></td>
                              <td className="px-3 py-1.5"><input type="text" value={p.description} onChange={e => updateCustomPlan(p.id, 'description', e.target.value)}
                                className="w-full px-2 py-1.5 text-xs bg-background-50 border border-background-200 rounded text-foreground-700" placeholder="Opsiyonel" /></td>
                              <td className="px-3 py-1.5"><button onClick={() => removeCustomPlan(p.id)} className="w-6 h-6 rounded flex items-center justify-center cursor-pointer text-red-400 hover:bg-red-50"><i className="ri-close-line text-sm"></i></button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {customPlans.length > 0 && (
                    <div className="bg-background-100/50 rounded-xl p-3 text-sm space-y-1">
                      <div className="flex justify-between"><span>Kalan</span><span>{formatTRY(plannedRemaining)}</span></div>
                      <div className="flex justify-between"><span>Plan Toplamı</span><span className={customIsBalanced ? 'text-emerald-600' : 'text-red-500'}>{formatTRY(customTotal)}</span></div>
                      {!customIsBalanced && <div className="flex justify-between"><span>Fark</span><span className="text-red-500">{formatTRY(Math.abs(customDiff))}</span></div>}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── TESLİMAT ── */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="w-8 h-8 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-truck-line text-background-50 text-sm"></i>
            </span>
            <h3 className="text-sm font-semibold text-foreground-900">Teslimat Bilgileri</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Tahmini Teslimat Tarihi</label><input type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" /></div>
            <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Tahmini Saat</label><input type="time" value={deliveryTime} onChange={e => setDeliveryTime(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" /></div>
            <div className="flex items-center gap-2 pt-5">
              <input type="checkbox" id="needAssembly" checked={needAssembly} onChange={e => setNeedAssembly(e.target.checked)} className="w-4 h-4 rounded border-background-300 cursor-pointer" />
              <label htmlFor="needAssembly" className="text-sm text-foreground-600 cursor-pointer whitespace-nowrap">Montaj Gerekli</label>
            </div>
            <div className="sm:col-span-2"><label className="block text-xs font-medium text-foreground-500 mb-1.5">Teslimat Adresi</label><textarea value={deliveryAddress} onChange={e => setDeliveryAddress(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 resize-none" rows={2}></textarea></div>
            <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Teslimat Notu</label><textarea value={deliveryNote} onChange={e => setDeliveryNote(e.target.value)} maxLength={500} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 resize-none" rows={2}></textarea></div>
          </div>
        </div>

        {/* ── TEKLİF AYARLARI ── */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="w-8 h-8 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-settings-3-line text-background-50 text-sm"></i>
            </span>
            <h3 className="text-sm font-semibold text-foreground-900">Teklif Ayarları</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Teklif Tarihi</label><input type="date" value={quoteDate} onChange={e => setQuoteDate(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" /></div>
            <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Geçerlilik Tarihi</label><input type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" /></div>
            <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Durum</label><select value={status} onChange={e => setStatus(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 cursor-pointer">{STATUS_LIST.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Hazırlayan</label><input type="text" value={preparedBy} onChange={e => setPreparedBy(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" /></div>
            <div className="sm:col-span-2"><label className="block text-xs font-medium text-foreground-500 mb-1.5">Müşteriye Özel Açıklama</label><textarea value={publicNote} onChange={e => setPublicNote(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 resize-none" rows={2} placeholder="Teklif belgesinde görünecek not"></textarea></div>
            <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">İç Personel Notu</label><textarea value={internalNote} onChange={e => setInternalNote(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 resize-none" rows={2} placeholder="Sadece personel görür"></textarea></div>
          </div>
        </div>

        {/* ── BUTTONLAR ── */}
        <div className="flex items-center gap-3 pt-2 justify-end flex-wrap">
          <button onClick={() => navigate('/quotes')} className="btn-outline cursor-pointer whitespace-nowrap">İptal</button>
          <button onClick={() => handleSubmit(true)} disabled={saving} className="px-4 py-2.5 rounded-lg bg-background-100 text-foreground-700 text-sm font-medium hover:bg-background-200 cursor-pointer whitespace-nowrap flex items-center gap-2">
            <i className="ri-save-line"></i> Taslak Kaydet
          </button>
          <button onClick={() => handleSubmit(false)} disabled={saving} className="btn-primary flex items-center gap-2 cursor-pointer whitespace-nowrap">
            {saving ? <><i className="ri-loader-4-line animate-spin"></i>Kaydediliyor...</> : <><i className="ri-check-line"></i><span>Teklifi Kaydet</span></>}
          </button>
        </div>
      </div>

      {/* ── Kategori Seçim Modalı ── */}
      {showCategoryPicker && (
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-4" onClick={() => setShowCategoryPicker(false)}>
          <div className="bg-background-50 rounded-2xl p-6 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="text-center mb-5">
              <span className="w-12 h-12 rounded-2xl bg-primary-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-stack-line text-primary-500 text-xl"></i>
              </span>
              <h3 className="text-lg font-semibold text-foreground-900">Ürün kategorisi seçin</h3>
            </div>
            <div className="space-y-2">
              <button onClick={() => addItem('furniture')} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-amber-200 bg-amber-50/50 hover:bg-amber-50 cursor-pointer transition-colors text-left">
                <span className="w-9 h-9 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0"><i className="ri-sofa-line text-background-50"></i></span>
                <div className="flex-1"><p className="text-sm font-medium text-foreground-800">Mobilya</p><p className="text-xs text-foreground-400">Koltuk, yatak odası, yemek odası...</p></div>
              </button>
              <button onClick={() => addItem('profilo')} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-sky-200 bg-sky-50/50 hover:bg-sky-50 cursor-pointer transition-colors text-left">
                <span className="w-9 h-9 rounded-lg bg-sky-500 flex items-center justify-center flex-shrink-0"><i className="ri-fridge-line text-background-50"></i></span>
                <div className="flex-1"><p className="text-sm font-medium text-foreground-800">Profilo</p><p className="text-xs text-foreground-400">Beyaz eşya, klima, ankastre...</p></div>
              </button>
              <button onClick={() => addItem('other')} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-foreground-200 bg-background-100/50 hover:bg-background-100 cursor-pointer transition-colors text-left">
                <span className="w-9 h-9 rounded-lg bg-foreground-500 flex items-center justify-center flex-shrink-0"><i className="ri-more-line text-background-50"></i></span>
                <div className="flex-1"><p className="text-sm font-medium text-foreground-800">Diğer</p><p className="text-xs text-foreground-400">Avize, perde, halı, dekorasyon...</p></div>
              </button>
            </div>
            <button onClick={() => setShowCategoryPicker(false)} className="w-full mt-3 px-4 py-2.5 text-sm text-foreground-500 hover:text-foreground-700 cursor-pointer rounded-lg hover:bg-background-100">Vazgeç</button>
          </div>
        </div>
      )}
    </div>
  );
}